import { ASSET_LIST_PAGE_SIZE } from '~/services/partner-management-service/constants';
import GatewayAxiosClient from '../GatewayAxiosClient';
import type {
  FulfillmentContext,
  FulfillmentContextDetail,
  AddressCreateParams,
  AddressUpdateParams,
  ContactCreateParams,
  ContactUpdateParams,
  Schedule,
  Contract,
  CarrierSettings,
  CarrierRateSet,
  LabelProperties,
  LabelPropertiesCreateParams,
  LabelPropertiesUpdateParams,
  Augmentation,
  ScheduleCreateParams,
  ScheduleUpdateParams,
  ReturnSetting,
  Packaging,
  PackagingSet,
  ThirdPartyBillingAccount,
  AssetTag,
  AugmentationSet,
  SelectionModifier,
  UPSRegistrationCreate,
  ValidateFedexAddressBody,
  GenerateFedexPinBody,
  ValidateFedexPinBody,
  FedexRegistration,
} from './types';
import { getToken } from '../../session';
import type {
  CarrierSettingsCreateParams,
  CarrierSettingsUpdateParams,
  ContractCreateParams,
  CarrierAccountUpdateParams,
  CarrierAccountCreateParams,
  CreateFulfillmentContextParams,
  CreateOriginParams,
  CreateTenantParams,
  LinkAddressParams,
  LinkCarrierAccountParams,
  LinkCarrierSettingsParams,
  LinkContactParams,
  LinkContractParams,
  LinkLabelPropertyParams,
  LinkOriginParams,
  LinkTagParams,
  UpdateFulfillmentContextParams,
  UpdateTenantParams,
  InjectionProfileUpdateParams,
  InjectionProfileCreateParams,
  LinkToInjectionProfileParams,
  LinkToInjectionSiteParams,
  CreateLabelAugmentationParams,
  LinkLabelAugmentationParams,
  UpdateLabelAugmentationParams,
  LinkScheduleParams,
  UpdateOriginParams,
  ContractUpdateParams,
  CreateThirdPartyBillingAccountParams,
  LinkThirdPartyBillingAccountParams,
  LinkThirdPartyBillingAccountSetParams,
  UnlinkTenant,
  LinkTenantParams,
  LinkReturnAddressOverrideParams,
  UnlinkReturnToAddressOverride,
  FetchDockLanePostalCodeMappingParams,
  LinkAddressToReturnToAddressOverride,
  LinkContactToReturnToAddressOverride,
} from '../../fulfillmentContext/types';
import type {
  PartnerAddressBookDocument,
  PartnerAddressV2,
  PartnerCarrierAccountV2,
  PartnerContact,
  PartnerInjectionSiteCreate,
  PartnerInjectionSiteUpdate,
  PartnerOrigin,
  PartnerReturnToAddressOverrideCreate,
  PartnerTenant,
  PartnerThirdPartyBillingAccountSet,
  PartnerThirdPartyBillingAccountSetCreate,
  PartnerThirdPartyBillingAccountSetUpdate,
} from '@shipium-corp/shipium-ts-types';
import type { ReturnToAddressOverride } from '../../assets/types';

export type ContextDetailMap = {
  contextId: string;
  tenantId?: string;
  thirdPartyBillingAccountSetIds?: string[];
};

export class FP_FulfillmentContextService {
  public async fetchFulfillmentContexts(): Promise<FulfillmentContext[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/fulfillmentContexts', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchTenants(): Promise<PartnerTenant[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/tenants', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchOrigins(): Promise<PartnerOrigin[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/origins', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchThirdPartyBillingAccounts(): Promise<ThirdPartyBillingAccount[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/thirdPartyBillingAccounts', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchThirdPartyBillingAccountSets(): Promise<Required<PartnerThirdPartyBillingAccountSet>[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchTags(): Promise<AssetTag[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/tags', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchContracts(): Promise<Contract[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/contracts', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchCarrierSettings(): Promise<CarrierSettings[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/carrierSettings', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchCarrierRateSets(): Promise<CarrierRateSet[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/carrierRateSets', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }
  public async fetchCarrierRateSet(carrierRateSetId: string): Promise<CarrierRateSet> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}`,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async fetchSelectionModifiers(): Promise<SelectionModifier[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/selectionModifiers', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchSchedules(): Promise<Schedule[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/schedules', {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async createSchedule(params: ScheduleCreateParams): Promise<Schedule> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/schedules', params, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchPackaging(): Promise<Packaging[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/packaging', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateSchedule(params: ScheduleUpdateParams): Promise<Schedule> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/schedules/${params.scheduleId}`,
      params,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async linkSchedule(params: LinkScheduleParams): Promise<void> {
    await GatewayAxiosClient({
      method: params.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/schedules/${params.scheduleId}/origins/${params.originId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async fetchPackagingSets(): Promise<PackagingSet[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/packagingSets', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchFulfillmentContextDetail(
    fulfillmentContextId: string,
    tenantId?: string,
    forceCacheRefresh = true,
  ): Promise<FulfillmentContextDetail> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/details/${fulfillmentContextId}`,
      {
        params: {
          tenantId,
          forceCacheRefresh,
          // Improvement would be to have Partner Management Service hydrate a set of thirdPartyBillingSetIDs
        },
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public static contextDetailMap(contexts: FulfillmentContext[], tenants: PartnerTenant[]): ContextDetailMap[] {
    const contextDetailMap: ContextDetailMap[] = [];
    const tenantLinkMap = tenants.reduce<Record<string, string | null>>((prev, curr) => {
      const contextLinks = curr.links?.fulfillmentContexts;
      if (Array.isArray(contextLinks) && contextLinks.length > 0) {
        for (const link of contextLinks) {
          prev[link.shipiumId] = curr.tenantId!;
        }
      }
      return prev;
    }, {});

    for (const context of contexts) {
      contextDetailMap.push({
        contextId: context.fulfillmentContextId,
        tenantId: tenantLinkMap[context.fulfillmentContextId] ?? undefined,
        thirdPartyBillingAccountSetIds: context.thirdPartyBillingAccountSetIds,
      });
    }

    return contextDetailMap;
  }

  public async createFulfillmentContext(params: CreateFulfillmentContextParams): Promise<string> {
    let tenantId = params.fulfillmentContext.tenantId;
    let returnToAddressOverrideId = params.fulfillmentContext.returnToAddressOverrideId;
    if (params.tenant) {
      tenantId = await this.createTenant(params.tenant as CreateTenantParams);
    }

    if (params.returnToAddressOverride) {
      returnToAddressOverrideId = await this.createReturnToAddressOverride(params.returnToAddressOverride);
      if (params.returnToAddressOverride.addressId) {
        await this.linkAddressToReturnAddressOverride({
          returnToAddressOverrideId,
          addressId: params.returnToAddressOverride.addressId,
        });
      }
      if (params.returnToAddressOverride.contactId) {
        await this.linkContactToReturnAddressOverride({
          returnToAddressOverrideId,
          contactId: params.returnToAddressOverride.contactId,
        });
      }
    }

    const contextRes = await GatewayAxiosClient.post(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts`,
      {
        name: params.fulfillmentContext.name,
        fulfillmentType: params.fulfillmentContext.fulfillmentType,
        ...(params.fulfillmentContext.partnerProvidedId && {
          partnerProvidedId: params.fulfillmentContext.partnerProvidedId,
        }),
        ...(params.fulfillmentContext.domain && { domain: params.fulfillmentContext.domain }),
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    const { fulfillmentContextId } = contextRes.data;

    if (tenantId && fulfillmentContextId) {
      await this.linkTenant({ tenantId, fulfillmentContextId });
    }

    if (tenantId && params.tenant?.usppiAddressId) {
      await this.linkAddress({
        addressType: 'usppiAddress',
        assetLinkType: 'tenants',
        addressId: params.tenant?.usppiAddressId,
        linkAction: 'link',
        fulfillmentContextId,
        assetId: tenantId,
      });
    }
    if (tenantId && params.tenant?.usppiContactId) {
      await this.linkContact({
        contactType: 'usppiContact',
        assetLinkType: 'tenants',
        contactId: params.tenant?.usppiContactId,
        linkAction: 'link',
        fulfillmentContextId,
        assetId: tenantId,
      });
    }
    if (returnToAddressOverrideId && fulfillmentContextId) {
      await this.linkReturnAddressOverride({ returnToAddressOverrideId, fulfillmentContextId });
    }

    return fulfillmentContextId;
  }

  public async deleteFulfillmentContext(contextId: string): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${contextId}`,
      {
        active: false,
        archived: true,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
  }

  public async updateFulfillmentContext(context: UpdateFulfillmentContextParams): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${context.fulfillmentContextId}`,
      {
        name: context.name,
        fulfillmentType: context.fulfillmentType,
        partnerProvidedId: context.partnerProvidedId,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
  }

  public async updateTenant(tenantParams: UpdateTenantParams): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantParams.tenantId}`,
      {
        name: tenantParams.name,
        partnerProvidedId: tenantParams.partnerProvidedId,
        taxIdentificationNumber: tenantParams.taxIdentificationNumber,
        ukimsNumber: tenantParams.ukimsNumber,
        eoriNumber: tenantParams.eoriNumber,
        vatNumber: tenantParams.vatNumber,
        tenantId: tenantParams.tenantId,
        originInfoMap: tenantParams.originInfoMap,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
  }

  public async createTenant(tenantParams: CreateTenantParams): Promise<string> {
    const tenantRes = await GatewayAxiosClient.post(
      `/partner-management-proxy/api/v1/partners/tenants`,
      {
        name: tenantParams.name,
        partnerProvidedId: tenantParams.partnerProvidedId,
        taxIdentificationNumber: tenantParams.taxIdentificationNumber,
        ukimsNumber: tenantParams.ukimsNumber,
        eoriNumber: tenantParams.eoriNumber,
        vatNumber: tenantParams.vatNumber,
        originInfoMap: tenantParams.originInfoMap,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return tenantRes.data.tenantId;
  }

  public async linkTenant(tenantParams: LinkTenantParams): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantParams.tenantId}/link`,
      {
        fulfillmentContexts: [{ id: tenantParams.fulfillmentContextId }],
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
  }

  public async unlinkTenant(tenantParams: UnlinkTenant): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantParams.tenantId}/unlink`,
      tenantParams.unlinkTenantParams,
    );
  }

  public async createReturnToAddressOverride(
    returnAddressOverrideParams: PartnerReturnToAddressOverrideCreate,
  ): Promise<string> {
    const returnToAddressOverrideRes = await GatewayAxiosClient.post(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides`,
      {
        name: returnAddressOverrideParams.name,
        partnerProvidedId: returnAddressOverrideParams.partnerProvidedId,
        description: returnAddressOverrideParams.description,
        carrierIds: returnAddressOverrideParams.carrierIds,
        tenantIds: returnAddressOverrideParams.tenantIds,
      },
    );
    return returnToAddressOverrideRes.data.returnToAddressOverrideId;
  }

  public async updateReturnAddressOverride(returnToAddressOverrideParams: any): Promise<ReturnToAddressOverride> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${returnToAddressOverrideParams.returnToAddressOverrideId}`,
      {
        name: returnToAddressOverrideParams.name,
        partnerProvidedId: returnToAddressOverrideParams.partnerProvidedId,
        description: returnToAddressOverrideParams.description,
        carrierIds: returnToAddressOverrideParams.carrierIds,
        tenantIds: returnToAddressOverrideParams.tenantIds,
      },
    );
    return res.data;
  }

  public async unlinkReturnToAddressOverrideFromFulfillmentContext(
    params: UnlinkReturnToAddressOverride,
  ): Promise<void> {
    await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${params.returnToAddressOverrideId}/fulfillmentContexts/${params.fulfillmentContextId}`,
    );
  }

  public async linkReturnToAddressOverrideFromFulfillmentContext(params: UnlinkReturnToAddressOverride): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${params.returnToAddressOverrideId}/fulfillmentContexts/${params.fulfillmentContextId}`,
    );
  }

  public async linkAddressToReturnAddressOverride(params: LinkAddressToReturnToAddressOverride): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${params.returnToAddressOverrideId}/addresses/${params.addressId}`,
    );
  }

  public async unlinkAddressFromReturnAddressOverride(params: LinkAddressToReturnToAddressOverride): Promise<void> {
    await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${params.returnToAddressOverrideId}/addresses/${params.addressId}`,
    );
  }

  public async linkContactToReturnAddressOverride(params: LinkContactToReturnToAddressOverride): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${params.returnToAddressOverrideId}/contacts/${params.contactId}`,
    );
  }

  public async unlinkContactFromReturnAddressOverride(params: LinkContactToReturnToAddressOverride): Promise<void> {
    await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/returnToAddressOverrides/${params.returnToAddressOverrideId}/contacts/${params.contactId}`,
    );
  }

  public async linkReturnAddressOverride(
    returnToAddressOverrideParams: LinkReturnAddressOverrideParams,
  ): Promise<void> {
    await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${returnToAddressOverrideParams.fulfillmentContextId}/returnToAddressOverrides/${returnToAddressOverrideParams.returnToAddressOverrideId}`,
    );
  }

  public async linkOrigin(linkOriginParams: LinkOriginParams): Promise<void> {
    if (linkOriginParams.linkAction === 'link') {
      await GatewayAxiosClient.put(
        `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${linkOriginParams.fulfillmentContextId}/origins/${linkOriginParams.originId}`,
        undefined,
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      );
    }
    if (linkOriginParams.linkAction === 'unlink') {
      await GatewayAxiosClient.delete(
        `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${linkOriginParams.fulfillmentContextId}/origins/${linkOriginParams.originId}`,
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      );
    }
  }

  public async linkTag(tagParams: LinkTagParams): Promise<void> {
    await GatewayAxiosClient({
      method: tagParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/tags/${tagParams.tagId}/fulfillmentContexts/${tagParams.fulfillmentContextId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async linkAddress(addressLinkParams: LinkAddressParams): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient({
      method: addressLinkParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/addresses/` +
        `${addressLinkParams.addressType === 'base' ? '' : addressLinkParams.addressType + '/'}` +
        `${addressLinkParams.addressId}/` +
        `${addressLinkParams.assetLinkType}/` +
        `${addressLinkParams.assetId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });

    return res.data;
  }

  public async fetchAddress(addressId: string): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/addresses/${addressId}`);
    return res.data;
  }

  public async fetchAddresses(): Promise<PartnerAddressV2[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/addresses`, {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchAddressBook(): Promise<PartnerAddressBookDocument[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/book/addresses/search`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async createOriginWithAssets(params: CreateOriginParams): Promise<string> {
    const originId = await this.createOrigin(params);

    await Promise.all([
      params.addressId &&
        (await this.linkAddress({
          addressType: 'base',
          assetLinkType: 'origins',
          addressId: params.addressId,
          linkAction: 'link',
          fulfillmentContextId: params.fulfillmentContextId,
          assetId: originId,
        })),
      params.contactId &&
        (await this.linkContact({
          contactType: 'base',
          assetLinkType: 'origins',
          contactId: params.contactId,
          linkAction: 'link',
          fulfillmentContextId: params.fulfillmentContextId,
          assetId: originId,
        })),
      params.scheduleId &&
        (await this.linkSchedule({
          scheduleId: params.scheduleId,
          originId,
          linkAction: 'link',
          fulfillmentContextId: params.fulfillmentContextId,
        })),
      params.returnToAddressId &&
        (await this.linkReturnAddressToOrigin({
          originId,
          addressId: params.returnToAddressId,
          linkAction: 'link',
        })),
      params.returnToContactId &&
        (await this.linkReturnContactToOrigin({
          originId,
          contactId: params.returnToContactId,
          linkAction: 'link',
        })),
    ]);

    return originId;
  }

  public async updateOriginWithAssets(params: UpdateOriginParams): Promise<void> {
    const originId = await this.updateOrigin(params);
    const origins = await this.fetchOrigins();
    const activeOrigin = origins.find((o) => o.originId === originId);
    await Promise.all([
      params.addressId !== activeOrigin?.addressId &&
        (await this.linkAddress({
          addressType: 'base',
          assetLinkType: 'origins',
          addressId: params.addressId,
          linkAction: 'link',
          fulfillmentContextId: params.fulfillmentContextId,
          assetId: params.originId,
        })),
      params.contactId !== activeOrigin?.contactId &&
        (await this.linkContact({
          contactType: 'base',
          assetLinkType: 'origins',
          contactId: params.contactId,
          linkAction: 'link',
          fulfillmentContextId: params.fulfillmentContextId,
          assetId: params.originId,
        })),
      params.scheduleId &&
        params.scheduleId !== activeOrigin?.scheduleId &&
        (await this.linkSchedule({
          scheduleId: params.scheduleId,
          originId: params.originId,
          linkAction: 'link',
          fulfillmentContextId: params.fulfillmentContextId,
        })),
      params.returnToAddressId !== activeOrigin?.returnToAddressId &&
        (await this.linkReturnAddressToOrigin({
          originId: params.originId,
          addressId: params.returnToAddressId || activeOrigin?.returnToAddressId || '',
          linkAction: params.returnToAddressId ? 'link' : 'unlink',
        })),
      params.returnToContactId !== activeOrigin?.returnToContactId &&
        (await this.linkReturnContactToOrigin({
          originId: params.originId,
          contactId: params.returnToContactId || activeOrigin?.returnToContactId || '',
          linkAction: params.returnToContactId ? 'link' : 'unlink',
        })),
    ]);
  }

  public async linkReturnAddressToOrigin(returnAddressParams: {
    originId: string;
    addressId: string;
    linkAction: 'link' | 'unlink';
  }): Promise<void> {
    await GatewayAxiosClient({
      method: returnAddressParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/origins/${returnAddressParams.originId}/addresses/returnTo/${returnAddressParams.addressId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async fetchReturnSettings(): Promise<ReturnSetting[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/returnsSettings`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async createAddress(params: AddressCreateParams): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/addresses', params, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateAddress(params: AddressUpdateParams): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/addresses/${params.addressId}`,
      params,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  // Archives the address (soft delete). The backend returns 409 if the address is still linked to
  // any other resource; in that case it is NOT archived and the caller should surface the conflict.
  public async deleteAddress(addressId: string): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.delete(`/partner-management-proxy/api/v2/partners/addresses/${addressId}`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async linkLabelProperty(linkLabelPropertyParams: LinkLabelPropertyParams): Promise<void> {
    await GatewayAxiosClient({
      method: linkLabelPropertyParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/labelProperties/` +
        `${linkLabelPropertyParams.labelPropertiesId}/fulfillmentContexts/` +
        `${linkLabelPropertyParams.fulfillmentContextId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async linkLabelAugmentation(linkLabelAugmentationParams: LinkLabelAugmentationParams): Promise<void> {
    const context = await this.fetchFulfillmentContextDetail(linkLabelAugmentationParams.fulfillmentContextId);
    const injectionSites = context.injectionProfiles.flatMap((ip) => ip.injectionSites);
    const selectedInjectionSite = injectionSites.find(
      (is) => is?.injectionSite?.injectionSiteId === linkLabelAugmentationParams.injectionSiteId,
    );
    let augmentationSetId = linkLabelAugmentationParams.injectionSiteId
      ? selectedInjectionSite?.injectionSite?.augmentationSetId
      : context.fulfillmentContext.augmentationSetId;
    if (!augmentationSetId) {
      const augmentationSetResponse = await GatewayAxiosClient.post(
        '/partner-management-proxy/api/v1/partners/augmentationSets',
        {
          name:
            (linkLabelAugmentationParams.injectionSiteId
              ? linkLabelAugmentationParams.injectionSiteId
              : context.fulfillmentContext.name) + ' (generated)',
          active: true,
          archived: false,
        },
        {
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        },
      );

      augmentationSetId = augmentationSetResponse.data.augmentationSetId;
      if (!linkLabelAugmentationParams.injectionSiteId) {
        await GatewayAxiosClient({
          method: linkLabelAugmentationParams.linkAction === 'link' ? 'PUT' : 'DELETE',
          url:
            `/partner-management-proxy/api/v1/partners/augmentationSets/` +
            `${augmentationSetId}/fulfillmentContexts/` +
            `${linkLabelAugmentationParams.fulfillmentContextId}`,
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        });
      } else {
        if (augmentationSetId) {
          await this.linkToInjectionSite({
            injectionSiteId: linkLabelAugmentationParams.injectionSiteId,
            linkType: 'augmentationSet',
            linkAction: 'link',
            linkedAssetId: augmentationSetId,
          });
        }
      }
    }
    if (linkLabelAugmentationParams.global === true) {
      await GatewayAxiosClient({
        method: linkLabelAugmentationParams.linkAction === 'link' ? 'PUT' : 'DELETE',
        url:
          `/partner-management-proxy/api/v1/partners/augmentationSets/` +
          `${augmentationSetId}/augmentations/` +
          `${linkLabelAugmentationParams.augmentationId}`,
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      });
    } else if (
      Array.isArray(linkLabelAugmentationParams.carrierIds) &&
      linkLabelAugmentationParams.carrierIds.length > 0
    ) {
      for (const carrierId of linkLabelAugmentationParams.carrierIds) {
        await GatewayAxiosClient({
          method: linkLabelAugmentationParams.linkAction === 'link' ? 'PUT' : 'DELETE',
          url:
            `/partner-management-proxy/api/v1/partners/augmentationSets/` +
            `${augmentationSetId}/carriers/${carrierId}/augmentations/` +
            `${linkLabelAugmentationParams.augmentationId}`,
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        });
      }
    } else if (
      Array.isArray(linkLabelAugmentationParams.carrierServiceMethodIdMap) &&
      linkLabelAugmentationParams.carrierServiceMethodIdMap.length > 0
    ) {
      for (const { carrierId, carrierServiceMethodId } of linkLabelAugmentationParams.carrierServiceMethodIdMap) {
        await GatewayAxiosClient({
          method: linkLabelAugmentationParams.linkAction === 'link' ? 'PUT' : 'DELETE',
          url:
            `/partner-management-proxy/api/v1/partners/augmentationSets/` +
            `${augmentationSetId}/carriers/${carrierId}/carrierServiceMethods/${carrierServiceMethodId}/augmentations/` +
            `${linkLabelAugmentationParams.augmentationId}`,
          headers: {
            authorization: `Bearer ${getToken()}`,
          },
        });
      }
    }
  }

  public async createLabelAugmentation(params: CreateLabelAugmentationParams): Promise<string> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/labelAugmentations',
      {
        name: params.name,
        zpl: params.zpl,
        shipmentTagToMatch: params.shipmentTagToMatch,
        injectionPosition: params.injectionPosition,
        active: true,
        archived: false,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    const labelAugmentationId = res.data.labelAugmentationId;
    await this.linkLabelAugmentation({
      linkAction: 'link',
      fulfillmentContextId: params.fulfillmentContextId,
      augmentationId: labelAugmentationId,
      global: params.global,
      carrierIds: params.carrierIds,
      injectionSiteId: params.injectionSiteId,
      carrierServiceMethodIdMap: params.carrierServiceMethodIdMap,
    });

    return labelAugmentationId;
  }

  public async updateLabelAugmentation(params: UpdateLabelAugmentationParams): Promise<string> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/labelAugmentations/${params.labelAugmentationId}`,
      {
        name: params.name,
        zpl: params.zpl,
        shipmentTagToMatch: params.shipmentTagToMatch,
        injectionPosition: params.injectionPosition,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async fetchLabelProperties(): Promise<LabelProperties[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/labelProperties`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchLabelAugmentations(): Promise<Augmentation[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/labelAugmentations`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchAugmentationSets(): Promise<AugmentationSet[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/augmentationSets`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async createLabelProperties(params: LabelPropertiesCreateParams): Promise<LabelProperties> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/labelProperties', params, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateLabelProperties(params: LabelPropertiesUpdateParams): Promise<LabelProperties> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/labelProperties/${params.labelPropertiesId}`,
      params,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async linkContact(contactLinkParams: LinkContactParams): Promise<void> {
    await GatewayAxiosClient({
      method: contactLinkParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contacts/` +
        `${contactLinkParams.contactType === 'base' ? '' : contactLinkParams.contactType + '/'}` +
        `${contactLinkParams.contactId}/` +
        `${contactLinkParams.assetLinkType}/` +
        `${contactLinkParams.assetId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async linkReturnContactToOrigin(returnContactParams: {
    originId: string;
    contactId: string;
    linkAction: 'link' | 'unlink';
  }): Promise<void> {
    await GatewayAxiosClient({
      method: returnContactParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v1/partners/origins/${returnContactParams.originId}/contacts/returnTo/${returnContactParams.contactId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async fetchContacts(): Promise<PartnerContact[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/contacts`, {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async createContact(params: ContactCreateParams): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/contacts', params, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateContact(params: ContactUpdateParams): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/contacts/${params.contactId}`,
      params,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async createOrigin(params: CreateOriginParams): Promise<string> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/origins',
      {
        name: params.name,
        partnerProvidedId: params.partnerProvidedId,
        originType: params.originType,
        description: params.description,
        active: true,
        archived: false,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data.originId;
  }

  public async updateOrigin(params: UpdateOriginParams): Promise<string> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/origins/${params.originId}`,
      {
        name: params.name,
        partnerProvidedId: params.partnerProvidedId,
        originType: params.originType,
        description: params.description,
        requireLaneMatch: params.requireLaneMatch,
        active: true,
        archived: false,
      },
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data.originId;
  }

  public async fetchCarrierAccounts(): Promise<PartnerCarrierAccountV2[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v2/partners/carrierAccounts`, {
      params: { count: ASSET_LIST_PAGE_SIZE },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async fetchCarrierAccount(carrierAccountId: string): Promise<PartnerCarrierAccountV2> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccountId}`,
    );
    return res.data;
  }

  public async updateCarrierSettings(params: CarrierSettingsUpdateParams) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/carrierSettings/${params.carrierSettingsId}`,
      params,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async createCarrierSettings(params: CarrierSettingsCreateParams) {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/carrierSettings`, params, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async linkCarrierSettings(payload: LinkCarrierSettingsParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contracts/` +
        `${payload.contractId}/carrierSettings/` +
        `${payload.carrierSettingsId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async linkCarrierContract(payload: LinkContractParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contracts/` +
        `${payload.contractId}/fulfillmentContexts/` +
        `${payload.fulfillmentContextId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async createContract(payload: ContractCreateParams) {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/contracts', payload, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateContract(payload: ContractUpdateParams) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/contracts/${payload.contractId}`,
      payload,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async linkCarrierAccount(payload: LinkCarrierAccountParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contracts/` +
        `${payload.contractId}/carrierAccounts/` +
        `${payload.carrierAccountId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }
  public async createCarrierAccount(payload: CarrierAccountCreateParams) {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v2/partners/carrierAccounts', payload, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }
  public async updateCarrierAccount(payload: CarrierAccountUpdateParams) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v2/partners/carrierAccounts/${payload.carrierAccountId}`,
      payload,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }
  public async fetchInjectionProfiles() {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/injectionProfile`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }
  public async fetchInjectionProfileById(identifier: string) {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/injectionProfile/${identifier}`,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }
  public async createInjectionProfile(payload: InjectionProfileCreateParams) {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/injectionProfile`, payload, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateInjectionProfile(payload: InjectionProfileUpdateParams) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/injectionProfile/${payload.injectionProfileId}`,
      payload,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async linkToInjectionProfile(payload: LinkToInjectionProfileParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/injectionProfile` +
        `/${payload.injectionProfileId}/${payload.linkType}/${payload.linkedAssetId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }
  public async fetchInjectionSites() {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/injectionSite`, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }
  public async createInjectionSite(payload: PartnerInjectionSiteCreate) {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/injectionSite`, payload, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async updateInjectionSite(payload: PartnerInjectionSiteUpdate & { injectionSiteId: string }) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/injectionSite/${payload.injectionSiteId}`,
      payload,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }

  public async linkToInjectionSite(payload: LinkToInjectionSiteParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/injectionSite/` +
        `${payload.injectionSiteId}/${payload.linkType}/` +
        `${payload.linkedAssetId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }
  public async fetchDockLanes(tenantId?: string) {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partnerConfiguration/lanes`, {
      params: {
        tenantId,
      },
    });
    return res.data.results;
  }
  public async fetchDockLane(identifier?: string) {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partnerConfiguration/lanes/${identifier}`,
    );
    return res.data;
  }
  public async fetchDockLaneSortConfiguration(laneSortConfigurationId?: string, originId?: string) {
    const params = originId ? { originId } : undefined;
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partnerConfiguration/laneSortConfigurations${
        laneSortConfigurationId ? '/' + laneSortConfigurationId : ''
      }`,
      { params },
    );
    return res.data;
  }
  public async fetchDockLaneSchedule(laneId: string) {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partnerConfiguration/lanes/${laneId}/schedule`,
    );
    return res.data.results;
  }
  public async fetchDockLanePostalMapping(params: FetchDockLanePostalCodeMappingParams) {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partnerConfiguration/origin/${params.originId}/laneSortConfiguration/${params.laneSortConfigurationId}/postalCodeMappings/${params.postalCodeMappingId}`,
    );
    return res.data;
  }
  public async createCarrierRateSet(payload: {
    name: string;
    description: string;
    partnerProvidedId: string;
    carrierId: string;
  }) {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/carrierRateSets`, payload, {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }

  public async createThirdPartyBillingAccount(payload: CreateThirdPartyBillingAccountParams) {
    const res = await GatewayAxiosClient({
      method: 'POST',
      data: payload,
      url: '/partner-management-proxy/api/v1/partners/thirdPartyBillingAccounts',
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });

    return res.data;
  }

  public async linkThirdPartyBillingAccount(payload: LinkThirdPartyBillingAccountParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets/` +
        `${payload.thirdPartyBillingAccountSetId}/thirdPartyBillingAccounts/` +
        `${payload.thirdPartyBillingAccountId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async createThirdPartyBillingAccountSet(
    payload: PartnerThirdPartyBillingAccountSetCreate,
  ): Promise<Required<PartnerThirdPartyBillingAccountSet>> {
    const response = await GatewayAxiosClient({
      method: 'POST',
      data: payload,
      url: '/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets',
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });

    return response.data;
  }

  public async updateThirdPartyBillingAccountSet({
    thirdPartyBillingAccountSetId,
    payload,
  }: {
    thirdPartyBillingAccountSetId: string;
    payload: PartnerThirdPartyBillingAccountSetUpdate;
  }): Promise<Required<PartnerThirdPartyBillingAccountSet>> {
    const response = await GatewayAxiosClient({
      method: 'PUT',
      data: payload,
      url: `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets/${thirdPartyBillingAccountSetId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });

    return response.data;
  }

  public async getThirdPartyBillingAccountSet({
    thirdPartyBillingAccountSetId,
  }: {
    thirdPartyBillingAccountSetId: string;
  }): Promise<Required<PartnerThirdPartyBillingAccountSet>> {
    const response = await GatewayAxiosClient({
      method: 'GET',
      url: `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets/${thirdPartyBillingAccountSetId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });

    return response.data;
  }

  public async linkThirdPartyBillingAccountSet(payload: LinkThirdPartyBillingAccountSetParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets/` +
        `${payload.thirdPartyBillingAccountSetId}/fulfillmentContexts/` +
        `${payload.fulfillmentContextId}`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
  }

  public async bulkLinkOriginToContexts(originId: string, fulfillmentContextIds: string[]) {
    const response = await GatewayAxiosClient({
      method: 'PUT',
      data: { fulfillmentContextIds },
      url: `/origins/${originId}/bulkLink`,
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return response.data;
  }
  public async fetchFedexRegistrations(partnerId: string) {
    const res = await GatewayAxiosClient.get(`/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex`);
    return res.data;
  }
  public async fetchFedexRegistration(partnerId: string, carrierAccountId: string) {
    const res = await GatewayAxiosClient.get(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}`,
    );
    const events = await GatewayAxiosClient.get(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}/events?eventType=addressValidated`,
    ).catch((e) => console.log(e));

    if (events && events?.data && res?.data) {
      res.data.carrierRegistrationEvents = events.data;
    }
    return res.data;
  }
  public async validateFedexAccountAddress(
    partnerId: string,
    carrierAccountId: string,
    address: ValidateFedexAddressBody,
  ) {
    const res = await GatewayAxiosClient.post(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}/address`,
      address,
    );
    return res.data;
  }
  public async validateFedexAccountInvoice(
    partnerId: string,
    carrierAccountId: string,
    invoice: ValidateFedexAddressBody,
  ) {
    const res = await GatewayAxiosClient.post(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}/invoice`,
      invoice,
    );
    return res.data;
  }
  public async generateFedexAccountPin(partnerId: string, carrierAccountId: string, pinBody: GenerateFedexPinBody) {
    const res = await GatewayAxiosClient.post(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}/pin`,
      pinBody,
    );
    return res.data;
  }
  public async validateFedexAccountPin(partnerId: string, carrierAccountId: string, pinBody: ValidateFedexPinBody) {
    const res = await GatewayAxiosClient.post(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}/pin/validate`,
      pinBody,
    );
    return res.data;
  }
  public async resetFedexRegistration(partnerId: string, carrierAccountId: string) {
    const res = await GatewayAxiosClient.patch(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/fedex/${carrierAccountId}`,
      {
        lifecycleStage: 'pending' as FedexRegistration['lifecycleStage'],
      },
    );
    return res.data;
  }
  public async fetchUpsRegistrations(partnerId: string) {
    const res = await GatewayAxiosClient.get(
      `/ups-registration-proxy/api/v1/carrier-connect/ups-register/${partnerId}/search`,
    );
    return res.data;
  }
  public async generateUPSRegistration(upsCreate: UPSRegistrationCreate) {
    const res = await GatewayAxiosClient.post(`/ups-registration-proxy/api/v1/carrier-connect/ups-register`, upsCreate);
    return res.data;
  }
  public async disconnectUPSRegistration(upsRegistrationId: string) {
    const res = await GatewayAxiosClient.put(
      `/ups-registration-proxy/api/v1/carrier-connect/ups-register/${upsRegistrationId}/disconnect`,
    );
    return res.data;
  }
  public async disconnectAmazonRegistration(partnerId: string, carrierAccountId: string) {
    const res = await GatewayAxiosClient.put(
      `/ups-registration-proxy/api/v1/carrier-connect/${partnerId}/amazon/${carrierAccountId}/disconnect`,
    );
    return res.data;
  }
}
